"use client";

import React from "react";
import { useT } from "@/hooks/useT";
import { useAuth } from "@/hooks/useAuth";
import CdnImage from "@/components/CdnImage";
import GradientAvatar from "@/app/components/GradientAvatar";

// Copy/Pause button with avatar and @username.
// Hidden when the row belongs to the current user — copying yourself is
// a no-op the engine rejects, and seeing your own pfp on a "Copy" button
// is just confusing.
export default function CopyPauseButton({
  wallet,
  username,
  avatar,
  followedTraders,
  onToggleCopy,
  onCopy,
}: {
  wallet: string;
  username: string;
  avatar?: string;
  followedTraders: Map<string, { leaderId: string; enabled: boolean }>;
  onToggleCopy: (wallet: string) => void;
  onCopy: () => void;
}) {
  const t = useT();
  const { currentWalletAddress } = useAuth();
  const normalizedWallet = wallet.toLowerCase();
  const copyInfo = normalizedWallet ? followedTraders.get(normalizedWallet) : undefined;
  if (!wallet) return null;
  if (currentWalletAddress && normalizedWallet === currentWalletAddress.toLowerCase()) return null;

  // Avatar element: real image when available, deterministic GradientAvatar
  // (same seed as the row avatar) when not. Mirrors the row avatar so the
  // Copy CTA always shows the trader's identity chip — no more "Copy @user"
  // floating without a leading visual.
  const avatarEl = avatar ? (
    <CdnImage src={avatar} alt={username} width={18} height={18} sizes="18px" className="w-[18px] h-[18px] rounded-[4px] object-cover" />
  ) : (
    <GradientAvatar seed={username || wallet || '?'} size={18} />
  );

  if (copyInfo) {
    return (
      <button
        onClick={() => onToggleCopy(normalizedWallet)}
        className={`flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-[5px] text-[11px] font-bold transition-colors border ${
          copyInfo.enabled
            ? 'border-orange-500/30 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20'
            : 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
        }`}
      >
        {avatarEl}
        {copyInfo.enabled ? t("activity.copyButton.pause") : t("activity.copyButton.resume")} @{username}
      </button>
    );
  }

  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-1 pl-1 pr-2 py-1 rounded-[5px] text-[11px] font-bold bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
    >
      {avatarEl}
      {t("activity.copyButton.copy")} @{username}
    </button>
  );
}
